/**
 * The ongoing Alice conversation, for every portal.
 *
 * ## The defect this replaces
 *
 * MPF and Cascade both carried this effect:
 *
 *     useEffect(() => {
 *       if (lastContextKeyRef.current === contextKey) return;
 *       lastContextKeyRef.current = contextKey;
 *       replaceMessages([WELCOME]);      // <- everything you said is gone
 *     }, [contextKey]);
 *
 * The intent was reasonable — do not let Alice answer about the payment you
 * just navigated away from — but the mechanism was wrong. It conflated two
 * different things:
 *
 *   * the CONVERSATION: what has been said, which belongs to the person and
 *     survives everything short of them ending it;
 *   * the PAGE CONTEXT: what they are looking at right now, which changes with
 *     every click and is an input to the NEXT turn, not a reason to forget the
 *     previous ones.
 *
 * Opening a payment, reading Alice's answer, then clicking through to the
 * linked contact to ask a follow-up is the most ordinary thing a user does in
 * these portals, and it silently wiped the thread every time.
 *
 * So the conversation lives here, keyed by identity, and the page context is
 * passed per turn by the panel. Changing pages changes what Alice is told about
 * the user's location; it does not touch the messages.
 *
 * ## Persistence and isolation
 *
 * Messages are held in `sessionStorage`, which is the honest match for their
 * lifetime: they survive navigation, remounts, panel open/close, docked/overlay
 * swaps, tab switches, and a reload, and they are gone when the browser session
 * is. They are never written to `localStorage` — a conversation left on a
 * shared machine after the tab closes is not something an admin portal should
 * do.
 *
 * The storage key includes an `identity` the caller composes from the signed-in
 * user, the organization, the app, and any support/impersonation session. A
 * different identity reads a different key, so signing in as someone else — or
 * entering a View-as-User session — cannot surface the previous conversation.
 * When identity is not yet known the hook holds messages in memory only rather
 * than writing them under a guessable key.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** The minimum a stored message must be. Portals may carry more. */
export interface AliceStoredMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** How many turns to retain. Old enough to be useful, small enough to store. */
const MAX_STORED_MESSAGES = 60;

/** Bumped when the stored shape changes, so an old payload is dropped, not misread. */
const STORAGE_VERSION = 'v1';

export interface UseAliceConversationOptions<TMessage extends AliceStoredMessage> {
  /** Portal-unique prefix, e.g. 'admin-mpf:alice'. */
  storageKey: string;
  /**
   * Who this conversation belongs to. Compose from user id, organization, app,
   * and impersonation session. `null` means identity is not resolved yet: the
   * hook keeps messages in memory and writes nothing.
   */
  identity: string | null;
  /** The opening message. Shown when a conversation is empty or cleared. */
  welcome: TMessage;
}

export interface AliceConversation<TMessage extends AliceStoredMessage> {
  messages: TMessage[];
  /** Replace the whole transcript. Persists synchronously. */
  setMessages: (next: TMessage[]) => void;
  /** Append one message to whatever is current. */
  append: (message: TMessage) => void;
  /**
   * Latest transcript without waiting for a re-render — for the async send
   * path, which must not append to a stale array.
   */
  read: () => TMessage[];
  /** The explicit "New conversation" action. */
  clear: () => void;
  /** True once anything beyond the welcome has been said. */
  hasHistory: boolean;
}

/**
 * A conversation that survives navigation and belongs to exactly one identity.
 */
export function useAliceConversation<TMessage extends AliceStoredMessage>({
  storageKey,
  identity,
  welcome,
}: UseAliceConversationOptions<TMessage>): AliceConversation<TMessage> {
  // The welcome is content, not identity: a copy edit to it must not be able to
  // change which conversation is read back.
  const welcomeRef = useRef(welcome);
  welcomeRef.current = welcome;

  const fullKey = useMemo(
    () => conversationStorageKey(storageKey, identity),
    [storageKey, identity],
  );

  const [messages, setMessagesState] = useState<TMessage[]>(() =>
    readStored<TMessage>(fullKey) ?? [welcome],
  );
  // Mirrors state so the send path can read the current transcript without
  // closing over a stale render.
  const messagesRef = useRef<TMessage[]>(messages);
  const loadedKeyRef = useRef<string | null>(fullKey);

  // Identity changed: load THAT identity's conversation. Never carry the
  // previous one across — that is the whole point of keying by identity.
  useEffect(() => {
    if (loadedKeyRef.current === fullKey) return;
    loadedKeyRef.current = fullKey;
    const next = readStored<TMessage>(fullKey) ?? [welcomeRef.current];
    messagesRef.current = next;
    setMessagesState(next);
  }, [fullKey]);

  const commit = useCallback(
    (next: TMessage[]) => {
      const trimmed = next.length > MAX_STORED_MESSAGES ? next.slice(-MAX_STORED_MESSAGES) : next;
      messagesRef.current = trimmed;
      setMessagesState(trimmed);
      writeStored(fullKey, trimmed);
    },
    [fullKey],
  );

  const append = useCallback(
    (message: TMessage) => commit([...messagesRef.current, message]),
    [commit],
  );

  const read = useCallback(() => messagesRef.current, []);

  const clear = useCallback(() => commit([welcomeRef.current]), [commit]);

  return {
    messages,
    setMessages: commit,
    append,
    read,
    clear,
    hasHistory: messages.length > 1,
  };
}

/**
 * Compose a conversation identity.
 *
 * Every part that could change who is talking is included. A support session
 * gets its own bucket so a One Up operator viewing as a user neither sees that
 * user's conversation nor leaves one behind in it.
 */
export function aliceConversationIdentity(parts: {
  userId?: string | null;
  organizationId?: string | null;
  appId?: string | null;
  /** Any non-empty value marks this as an impersonation/support session. */
  supportSessionId?: string | null;
}): string | null {
  if (!parts.userId) return null;
  return [
    parts.userId,
    parts.organizationId ?? 'no-org',
    parts.appId ?? 'no-app',
    parts.supportSessionId ? `support:${parts.supportSessionId}` : 'self',
  ].join('|');
}

/**
 * Where one identity's conversation lives.
 *
 * Exported because this — not the React wiring — is where the isolation
 * guarantee actually lives, and it is worth testing directly. `null` identity
 * yields `null`: unknown identity must not produce a storage key at all.
 */
export function conversationStorageKey(
  storageKey: string,
  identity: string | null,
): string | null {
  return identity ? `${storageKey}:${STORAGE_VERSION}:${identity}` : null;
}

export function readStoredConversation<TMessage extends AliceStoredMessage>(
  key: string | null,
): TMessage[] | null {
  return readStored<TMessage>(key);
}

export function writeStoredConversation<TMessage extends AliceStoredMessage>(
  key: string | null,
  messages: TMessage[],
): void {
  writeStored(key, messages.length > MAX_STORED_MESSAGES ? messages.slice(-MAX_STORED_MESSAGES) : messages);
}

function readStored<TMessage extends AliceStoredMessage>(key: string | null): TMessage[] | null {
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // A stored payload is only as trustworthy as its shape. Anything that is
    // not a well-formed message list is discarded rather than rendered.
    const messages = parsed.filter(isStoredMessage) as TMessage[];
    return messages.length > 0 ? messages : null;
  } catch {
    return null;
  }
}

function writeStored<TMessage extends AliceStoredMessage>(
  key: string | null,
  messages: TMessage[],
): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(messages));
  } catch {
    // A full or blocked sessionStorage must not break the panel. The
    // conversation simply stops surviving reloads.
  }
}

function isStoredMessage(value: unknown): value is AliceStoredMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as { role?: unknown; content?: unknown };
  return (
    (message.role === 'user' || message.role === 'assistant') && typeof message.content === 'string'
  );
}
